import React, { useCallback, useEffect, useState } from "react";
import "./FyersAccessToken.css";
import { environment } from "environment/environment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ACCOUNT_NAMES = ["NK", "PK", "BH", "GH"];
const INDEX_API_ACCOUNTS = new Set(["NK", "PK"]);

const getAccountApiConfig = (accountName) => {
    if (INDEX_API_ACCOUNTS.has(accountName)) {
        return {
            apiName: "INDEX_API",
            getTokensUrl: `${environment.baseUrl}/index-api-fyersone-login/get-all-index-api-fyers-token`,
            loginUrl: `${environment.baseUrl}/index-api-fyersone-login/login`
        };
    }

    return {
        apiName: "STOCK_API",
        getTokensUrl: `${environment.stockApiBaseUrl}/stock-api-fyersone-login/get-all-stock-api-fyers-token`,
        loginUrl: `${environment.stockApiBaseUrl}/stock-api-fyersone-login/login`
    };
};

export default function FyersAccesstoken() {
    const [tokensByAccount, setTokensByAccount] = useState(() =>
        Object.fromEntries(ACCOUNT_NAMES.map(accountName => [accountName, []]))
    );
    const [generatingAccounts, setGeneratingAccounts] = useState(() =>
        Object.fromEntries(ACCOUNT_NAMES.map(accountName => [accountName, false]))
    );

    /* ================= INDEX API ================= */
    const fetchFyersTokens = useCallback(async (selectedAccountName) => {
        try {
            const fetchTokensByAccount = (accountName) => {
                const { apiName, getTokensUrl } = getAccountApiConfig(accountName);

                return fetch(
                    `${getTokensUrl}?apiName=${apiName}&accountName=${accountName}`
                ).then(r => r.json());
            };

            const filterTokensByAccount = (response, accountName) => {
                if (!response.status || !Array.isArray(response.data)) return [];

                return response.data.filter(
                    row => String(row.accountName ?? "").trim().toUpperCase() === accountName
                );
            };

            const accountNames = selectedAccountName
                ? [selectedAccountName]
                : ACCOUNT_NAMES;

            const accountResponses = await Promise.all(
                accountNames.map(async accountName => ({
                    accountName,
                    response: await fetchTokensByAccount(accountName)
                }))
            );

            setTokensByAccount(previous => {
                const updatedTokens = { ...previous };

                accountResponses.forEach(({ accountName, response }) => {
                    updatedTokens[accountName] = filterTokensByAccount(response, accountName);
                });

                return updatedTokens;
            });
        } catch {
            toast.error("Failed to load Fyers API tokens");
        }
    }, []);

    useEffect(() => {
        fetchFyersTokens();
    }, [fetchFyersTokens]);

    const generateAccountToken = async (accountName) => {
        const { apiName, loginUrl } = getAccountApiConfig(accountName);

        setGeneratingAccounts(previous => ({
            ...previous,
            [accountName]: true
        }));
        try {
            const res = await fetch(
                `${loginUrl}?accountName=${accountName}`
            ).then(r => r.json());

            if (res.status) {
                toast.success(`${accountName} ${apiName} token generated`);
                await fetchFyersTokens();
            }
        } catch {
            toast.error(`${accountName} ${apiName} generation failed`);
        } finally {
            setGeneratingAccounts(previous => ({
                ...previous,
                [accountName]: false
            }));
        }
    };

    /* ================= TABLE ================= */
    const renderTable = (data) => (
        <table className="responsive-table table-bordered text-center sticky-header full-height-table">
            <colgroup>
                <col className="date-column" />
                <col className="time-column" />
                <col className="generated-by-column" />
                <col className="api-name-column" />
                <col className="account-name-column" />
                <col className="token-column" />
            </colgroup>
            <thead>
                <tr>
                    <th>DATE</th>
                    <th>TIME</th>
                    <th>GENERATED BY</th>
                    <th>API NAME</th>
                    <th>ACCOUNT NAME</th>
                    <th>FYERS ACCESS TOKEN</th>
                </tr>
            </thead>
            <tbody>
                {data.length ? (
                    data.map((row, i) => (
                        <tr key={i}>
                            <td>{row.createdDate ?? "--"}</td>
                            <td>{row.createdTime ?? "--"}</td>
                            <td>{row.generatedBy ?? "--"}</td>
                            <td>{row.apiName ?? "--"}</td>
                            <td>{row.accountName ?? "--"}</td>
                            <td>
                                <span className="token-box">
                                    {row.fyersAccessToken ?? "--"}
                                </span>
                            </td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan="6">No data available</td>
                    </tr>
                )}
            </tbody>
        </table>
    );

    return (
        <div className="fyers-access-token-page full-page">
            <ToastContainer position="top-center" />

            {/* ===== HEADER (UNCHANGED) ===== */}
            <header className="header">
                <nav className="nav-container">

                    {/* LEFT SIDE - LOGO */}
                    <div className="left-nav">
                        <div className="logo-container">
                            <img
                                src="/assets/images/noptionwave-logo-1.png"
                                className="header-logo"
                                alt="noptionwave logo"
                            />
                        </div>
                    </div>

                </nav>
            </header>

            <div className="account-content full-page-content">
                <div className="table-container full-page-table">
                    <div className="index-account-tables">
                        {ACCOUNT_NAMES.map(accountName => (
                            <section className="account-table-section" key={accountName}>
                                <div className="account-table-header">
                                    <h4 className="account-table-title">
                                        {accountName} Account ({tokensByAccount[accountName].length})
                                    </h4>
                                    <button
                                        type="button"
                                        className="generate-token-btn"
                                        aria-label={`Generate Token ${accountName}`}
                                        onClick={() => generateAccountToken(accountName)}
                                        disabled={generatingAccounts[accountName]}
                                    >
                                        {generatingAccounts[accountName]
                                            ? `Generating ${accountName}...`
                                            : `Generate Token ${accountName}`}
                                    </button>
                                </div>
                                {renderTable(tokensByAccount[accountName])}
                            </section>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
